import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Music, Video, Image, X, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const UploadPage = () => {
  const { user, token } = useAuth();
  const [uploadType, setUploadType] = useState('track');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [permission, setPermission] = useState(null);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [availableTracks, setAvailableTracks] = useState([]);
  
  // Form states
  const [trackForm, setTrackForm] = useState({
    title: '',
    album: '',
    genre: '',
    file: null,
    thumbnail: null
  });
  
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    file: null,
    thumbnail: null,
    backgroundTrackId: ''
  });
  
  const [imageForm, setImageForm] = useState({
    caption: '',
    file: null,
    backgroundTrackId: ''
  });

  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      checkUploadPermission();
      fetchAvailableTracks();
    }
  }, [token]);

  const checkUploadPermission = async () => {
    setCheckingPermission(true);
    try {
      // We'll check permission when they try to upload
      // For now, just check if they're logged in
      setPermission({ allowed: true });
    } catch (error) {
      console.error('Permission check failed:', error);
    } finally {
      setCheckingPermission(false);
    }
  };

  const fetchAvailableTracks = async () => {
    try {
      const [userTracks, seededTracks] = await Promise.all([
        axios.get(`${API}/user-tracks?limit=50`),
        axios.get(`${API}/tracks?limit=50`)
      ]);
      setAvailableTracks([...userTracks.data, ...seededTracks.data]);
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
    }
  };

  const getCloudinarySignature = async (resourceType, folder) => {
    const response = await axios.get(`${API}/cloudinary/signature`, {
      params: { resource_type: resourceType, folder },
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const uploadToCloudinary = async (file, resourceType, folder, onProgress) => {
    const sig = await getCloudinarySignature(resourceType, folder);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
      formData,
      {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    );

    return response.data;
  };

  const handleTrackUpload = async (e) => {
    e.preventDefault();
    if (!trackForm.file || !trackForm.title) {
      toast.error('Please provide a title and audio file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload audio file
      const audioResult = await uploadToCloudinary(
        trackForm.file,
        'video', // Cloudinary uses 'video' for audio files
        'tracks',
        setUploadProgress
      );

      // Upload thumbnail if provided
      let thumbnailUrl = null;
      if (trackForm.thumbnail) {
        const thumbResult = await uploadToCloudinary(
          trackForm.thumbnail,
          'image',
          'thumbnails',
          () => {}
        );
        thumbnailUrl = thumbResult.secure_url;
      }

      // Save to backend
      const response = await axios.post(`${API}/upload/track`, {
        title: trackForm.title,
        album: trackForm.album || null,
        genre: trackForm.genre || null,
        cloudinary_public_id: audioResult.public_id,
        audio_url: audioResult.secure_url,
        thumbnail_url: thumbnailUrl,
        duration: audioResult.duration
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Track uploaded successfully!');
      setTrackForm({ title: '', album: '', genre: '', file: null, thumbnail: null });
      
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    if (!videoForm.file || !videoForm.title) {
      toast.error('Please provide a title and video file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload video file
      const videoResult = await uploadToCloudinary(
        videoForm.file,
        'video',
        'videos',
        setUploadProgress
      );

      // Upload thumbnail if provided
      let thumbnailUrl = null;
      if (videoForm.thumbnail) {
        const thumbResult = await uploadToCloudinary(
          videoForm.thumbnail,
          'image',
          'thumbnails',
          () => {}
        );
        thumbnailUrl = thumbResult.secure_url;
      }

      // Save to backend
      await axios.post(`${API}/upload/video`, {
        title: videoForm.title,
        description: videoForm.description || null,
        cloudinary_public_id: videoResult.public_id,
        video_url: videoResult.secure_url,
        thumbnail_url: thumbnailUrl,
        duration: videoResult.duration,
        background_track_id: videoForm.backgroundTrackId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Video uploaded successfully!');
      setVideoForm({ title: '', description: '', file: null, thumbnail: null, backgroundTrackId: '' });
      
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!imageForm.file || !imageForm.caption || !imageForm.backgroundTrackId) {
      toast.error('Please provide a caption, image, and select background music');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload image
      const imageResult = await uploadToCloudinary(
        imageForm.file,
        'image',
        'posts',
        setUploadProgress
      );

      // Save to backend
      await axios.post(`${API}/upload/image-post`, {
        caption: imageForm.caption,
        cloudinary_public_id: imageResult.public_id,
        image_url: imageResult.secure_url,
        background_track_id: imageForm.backgroundTrackId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Post created successfully!');
      setImageForm({ caption: '', file: null, backgroundTrackId: '' });
      
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <AlertCircle size={48} className="text-muted-foreground mb-4" />
        <h2 className="text-xl font-bold mb-2">Login Required</h2>
        <p className="text-muted-foreground">Please log in to upload content</p>
      </div>
    );
  }

  if (checkingPermission) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="upload-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold mb-2">Upload Content</h1>
        <p className="text-muted-foreground">Share your music, videos, and posts with the community</p>
      </motion.div>

      <Tabs value={uploadType} onValueChange={setUploadType} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="track" className="flex items-center gap-2">
            <Music size={16} /> Music
          </TabsTrigger>
          <TabsTrigger value="video" className="flex items-center gap-2">
            <Video size={16} /> Video
          </TabsTrigger>
          <TabsTrigger value="image" className="flex items-center gap-2">
            <Image size={16} /> Post
          </TabsTrigger>
        </TabsList>

        {/* Music Upload */}
        <TabsContent value="track">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="text-purple-400" /> Upload Music Track
              </CardTitle>
              <CardDescription>
                Share your music with Twin Cities fans. Only approved artists and bands can upload music.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrackUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="track-title">Track Title *</Label>
                  <Input
                    id="track-title"
                    value={trackForm.title}
                    onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })}
                    placeholder="Enter track title"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="track-album">Album</Label>
                    <Input
                      id="track-album"
                      value={trackForm.album}
                      onChange={(e) => setTrackForm({ ...trackForm, album: e.target.value })}
                      placeholder="Album name (optional)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="track-genre">Genre</Label>
                    <Input
                      id="track-genre"
                      value={trackForm.genre}
                      onChange={(e) => setTrackForm({ ...trackForm, genre: e.target.value })}
                      placeholder="e.g., Hip Hop, Rock"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Audio File *</Label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      trackForm.file ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-primary'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {trackForm.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <Check className="text-green-500" />
                        <span>{trackForm.file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setTrackForm({ ...trackForm, file: null }); }}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                        <p className="text-muted-foreground">Click to upload audio file</p>
                        <p className="text-xs text-muted-foreground mt-1">MP3, WAV (max 50MB)</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => setTrackForm({ ...trackForm, file: e.target.files[0] })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cover Art (optional)</Label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                      trackForm.thumbnail ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-primary'
                    }`}
                    onClick={() => thumbnailInputRef.current?.click()}
                  >
                    {trackForm.thumbnail ? (
                      <div className="flex items-center justify-center gap-2">
                        <img 
                          src={URL.createObjectURL(trackForm.thumbnail)} 
                          alt="Cover" 
                          className="w-16 h-16 object-cover rounded"
                        />
                        <span className="text-sm">{trackForm.thumbnail.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setTrackForm({ ...trackForm, thumbnail: null }); }}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Click to add cover art</p>
                    )}
                  </div>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setTrackForm({ ...trackForm, thumbnail: e.target.files[0] })}
                  />
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      Upload Track
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Video Upload */}
        <TabsContent value="video">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="text-blue-400" /> Upload Video
              </CardTitle>
              <CardDescription>
                Share promotional videos, shorts, or clips. Add background music from our library!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVideoUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="video-title">Title *</Label>
                  <Input
                    id="video-title"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    placeholder="Enter video title"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="video-description">Description</Label>
                  <Textarea
                    id="video-description"
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    placeholder="Describe your video..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Video File *</Label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      videoForm.file ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-primary'
                    }`}
                    onClick={() => document.getElementById('video-file')?.click()}
                  >
                    {videoForm.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <Check className="text-green-500" />
                        <span>{videoForm.file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setVideoForm({ ...videoForm, file: null }); }}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Video className="mx-auto mb-2 text-muted-foreground" size={32} />
                        <p className="text-muted-foreground">Click to upload video</p>
                        <p className="text-xs text-muted-foreground mt-1">MP4, WebM (max 100MB)</p>
                      </>
                    )}
                  </div>
                  <input
                    id="video-file"
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => setVideoForm({ ...videoForm, file: e.target.files[0] })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Background Music (optional)</Label>
                  <Select
                    value={videoForm.backgroundTrackId}
                    onValueChange={(value) => setVideoForm({ ...videoForm, backgroundTrackId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a track from our library" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTracks.map((track) => (
                        <SelectItem key={track.id} value={track.id}>
                          {track.title} - {track.artist_name || track.artist}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      Upload Video
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Image Post Upload */}
        <TabsContent value="image">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="text-pink-400" /> Create Post
              </CardTitle>
              <CardDescription>
                Share an image post with background music. Music is required for image posts!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleImageUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="image-caption">Caption *</Label>
                  <Textarea
                    id="image-caption"
                    value={imageForm.caption}
                    onChange={(e) => setImageForm({ ...imageForm, caption: e.target.value })}
                    placeholder="Write a caption..."
                    rows={3}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Image *</Label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      imageForm.file ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-primary'
                    }`}
                    onClick={() => document.getElementById('image-file')?.click()}
                  >
                    {imageForm.file ? (
                      <div className="flex flex-col items-center gap-2">
                        <img 
                          src={URL.createObjectURL(imageForm.file)} 
                          alt="Preview" 
                          className="max-w-[200px] max-h-[200px] object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setImageForm({ ...imageForm, file: null }); }}
                        >
                          <X size={16} className="mr-1" /> Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Image className="mx-auto mb-2 text-muted-foreground" size={32} />
                        <p className="text-muted-foreground">Click to upload image</p>
                        <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP</p>
                      </>
                    )}
                  </div>
                  <input
                    id="image-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setImageForm({ ...imageForm, file: e.target.files[0] })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Background Music * <span className="text-xs text-muted-foreground">(required)</span></Label>
                  <Select
                    value={imageForm.backgroundTrackId}
                    onValueChange={(value) => setImageForm({ ...imageForm, backgroundTrackId: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select background music" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTracks.map((track) => (
                        <SelectItem key={track.id} value={track.id}>
                          {track.title} - {track.artist_name || track.artist}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Creating Post...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      Create Post
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UploadPage;
